/**
 * FairwayBid – Inhalt der Seite "Über uns"
 *
 * Der Text stand vorher als ein langer Fließtext in der Console: neun
 * Abschnitte hintereinander, alle gleich aussehend, der wichtigste ganz
 * unten. Wichtig ist hier aber nicht alles gleich viel – die Offenlegung,
 * dass ein Händler derselben Gruppe auf dem Marktplatz auftreten kann, ist
 * das Stück, an dem eine Golfanlage entscheidet, ob sie uns traut.
 *
 * Deshalb steht der Inhalt hier gegliedert: Ausgangslage als zwei Seiten
 * eines Problems, die Idee als Pointe, Ziele als Karten, die Offenlegung in
 * einem eigenen Kasten mit den drei Regeln als Liste.
 *
 * Der Name der Betreiberin kommt aus imprint.js, die E-Mail-Adresse
 * ebenfalls – hier steht keine zweite Wahrheit.
 */
import { BETREIBER, KONTAKT } from './imprint';

export const ABOUT_PAGE_ID = 'about';
export const ABOUT_TYPE = 'fwb-ueber-uns';

export const EYEBROW = 'Über FairwayBid';
export const LEAD =
  'Einkaufen für die Golfanlage kostet Zeit, die im Betrieb niemand hat. FairwayBid dreht den Ablauf um: Sie schreiben aus, die Anbieter kommen zu Ihnen.';

/**
 * Die Ausgangslage als zwei Seiten desselben Problems. Nebeneinander sieht
 * man in einem Blick, was der Text vorher in zwei Absätzen erklärt hat:
 * Beide suchen einander, beide finden sich nicht.
 */
export const AUSGANGSLAGE = {
  titel: 'Zwei Seiten, dasselbe Problem',
  seiten: [
    {
      key: 'anlage',
      rolle: 'Die Golfanlage',
      headline: 'sucht Anbieter',
      text:
        'Rangebälle, Ballautomaten, Matten, Netze, Pflegetechnik, Carts, Ersatzteile – und jedes Mal derselbe Weg: Kataloge wälzen, drei Anbieter anrufen, auf Rückrufe warten, Angebote per E-Mail einsammeln, am Ende auf einem Zettel vergleichen. Das kostet Stunden. Und am Ende bleibt die Frage, ob man nicht doch ein besseres Angebot übersehen hat.',
    },
    {
      key: 'haendler',
      rolle: 'Der Händler',
      headline: 'sucht Bedarf',
      text:
        'Auf der anderen Seite stehen Händler und Hersteller, die genau diese Anfragen suchen – und sie über Kaltakquise, Messen und Streuwerbung mühsam finden müssen. Sie rufen Anlagen an, die gerade nichts brauchen, und verpassen die, die gerade etwas brauchen.',
    },
  ],
  fazit: 'Beide suchen einander. Beide finden sich nicht.',
};

/** Die Idee – die Pointe der Seite, deshalb dunkel und über die volle Breite. */
export const IDEE = {
  kern: 'FairwayBid dreht den Ablauf um.',
  absaetze: [
    'Die Golfanlage beschreibt einmal, was sie braucht: Kategorie, Menge oder Ausstattung, Zustand, gewünschte Leistungen, Lieferland und Wunschtermin. Diese Anfrage geht an die Anbieter, die genau diese Kategorien liefern und genau diese Region bedienen.',
    'Die Anlage bekommt Angebote statt Anrufe und vergleicht sie an einem Ort. Für Golfanlagen ist das dauerhaft kostenlos; Händler und Hersteller wählen eine Mitgliedschaft, die zu ihrem Anfragevolumen passt.',
  ],
};

export const ZIELE = {
  titel: 'Was wir erreichen wollen',
  punkte: [
    {
      label: 'Transparenz',
      text: 'Vergleichbare Angebote statt Preisen, die davon abhängen, wen man kennt.',
    },
    { label: 'Zeit', text: 'Eine Ausschreibung statt zehn Telefonaten.' },
    {
      label: 'Fairer Marktzugang',
      text:
        'Auch kleinere Anbieter und Spezialisten erreichen Anfragen, an die sie sonst nie gekommen wären.',
    },
    {
      label: 'Verlässlichkeit',
      text: 'Jede Anmeldung wird geprüft. Auf beiden Seiten sitzen verifizierte Betriebe.',
    },
  ],
};

export const KI = {
  titel: 'Was KI hier macht',
  lead:
    'Beschaffung scheitert selten am Willen, sondern daran, dass Bedarf und passender Lieferant sich nicht finden. Genau da setzen wir KI ein.',
  punkte: [
    {
      label: 'Die richtigen Anbieter statt Streuung',
      text:
        'Jede Anfrage wird nach Kategorie, Ausstattung, Menge, Liefergebiet und Wunschtermin ausgewertet und genau den Händlern zugestellt, die dazu passen. Die Anlage bekommt nicht zwanzig beliebige Angebote, sondern die wenigen, die zählen.',
    },
    {
      label: 'Anfragen, die von Anfang an vollständig sind',
      text:
        'Beim Einstellen führen wir Schritt für Schritt durch die Angaben, die ein Anbieter für ein belastbares Angebot braucht. Das erspart die übliche Rückfrageschleife.',
    },
    {
      label: 'Angebote, die vergleichbar sind',
      text:
        'Alle Angebote kommen in derselben Struktur zurück: Preis, Leistung und Liefertermin stehen immer an derselben Stelle. Vergleichen dauert Minuten statt eines Nachmittags.',
    },
    {
      label: 'Es wird besser, je mehr läuft',
      text:
        'Mit jeder Ausschreibung lernen wir genauer, welcher Anbieter zu welchem Bedarf passt. Kürzere Wege, weniger Leerlauf, bessere Treffer.',
    },
  ],
  grenze:
    'Entschieden wird nicht von der Maschine. KI sortiert, strukturiert und beschleunigt – welches Angebot angenommen wird, entscheidet allein die Golfanlage.',
};

/**
 * Die Transparenznotiz.
 *
 * Sie beantwortet die Frage, die sich ein Clubmanager stellt, bevor er
 * seinen Bedarf öffentlich macht: Bietet der Betreiber hier selbst mit?
 *
 * Bewusst knapp und ohne grossen Auftritt. Ein Absatz, der sich lange
 * rechtfertigt, weckt genau den Verdacht, den er ausräumen soll – die
 * Antwort steht in vier Wörtern, der Rest ist Begründung.
 *
 * Das ist ein Versprechen, kein Werbetext: Sollte je ein Unternehmen der
 * Gruppe als Anbieter auf FairwayBid auftreten, muss dieser Text geändert
 * werden, bevor das passiert – nicht danach.
 */
export const TRANSPARENZ = {
  eyebrow: 'Transparenz',
  frage: 'Bieten wir hier selbst mit?',
  antwort: 'Nein.',
  text: `Kein Unternehmen der ${BETREIBER.name} ist auf FairwayBid als Anbieter registriert. Wer den Marktplatz betreibt, sähe Anfragen früher und kennte fremde Angebote – diesen Interessenkonflikt wollen wir nicht. Deshalb betreibt eine Holding die Plattform und kein Handelsunternehmen.`,
  pruefbar: 'Prüfbar: Jeder Anbieter tritt hier mit seinem Firmennamen auf.',
};

export const KONTAKT_BLOCK = {
  titel: 'Reden Sie mit uns',
  text: `FairwayBid ist ein Angebot der ${BETREIBER.name}; wir kommen selbst aus dem Golfgeschäft und kennen beide Seiten. Das Portal steht am Anfang – wenn Ihnen etwas fehlt oder ein Ablauf umständlich ist, schreiben Sie uns. In dieser Phase hat Ihr Feedback direkten Einfluss darauf, was als Nächstes gebaut wird.`,
  email: KONTAKT.email,
};
